import { INSPECTION_AREA_NAME_MAX, INSPECTION_ITEM_NAME_MAX, INSPECTION_LINES_MAX } from './constants/index.ts';
import type { SupportedLanguage } from './constants/languages.ts';

/**
 * What a walkthrough checks, listed before anybody walks it.
 *
 * An inspection scored against lines typed on the spot measures whatever the
 * supervisor happened to think of that day, and two walkthroughs of the same
 * building stop being comparable. These are the lists a walkthrough can start
 * from instead: the building's own cleaning checklist — the work the crew is
 * actually paid to do there — or the standard walkthrough below.
 */

/** One line to check, before it has been scored. */
export interface InspectionLineSeed {
  areaName: string;
  itemName: string;
}

type Translated = Readonly<Record<SupportedLanguage, string>>;

/**
 * The standard walkthrough: the areas and checks a janitorial quality
 * inspection covers in almost every commercial building.
 *
 * Worded as the result an inspector looks for ("Floors mopped, no odor"),
 * not the task the crew performs ("Mop floors"), because that is what gets
 * scored. Every entry carries every supported language — the type refuses one
 * that does not, so adding a check is a compile error until it is translated.
 */
export const STANDARD_INSPECTION_AREAS: ReadonlyArray<{ area: Translated; items: readonly Translated[] }> = [
  {
    area: { en: 'Entrance & lobby', es: 'Entrada y vestíbulo' },
    items: [
      { en: 'Entry glass and doors free of smudges', es: 'Vidrios y puertas de entrada sin manchas' },
      { en: 'Floors and walk-off mats clean', es: 'Pisos y tapetes de entrada limpios' },
      { en: 'Reception desk and ledges dusted', es: 'Recepción y repisas sin polvo' },
      { en: 'Trash emptied and liners replaced', es: 'Basura vaciada y bolsas cambiadas' },
    ],
  },
  {
    area: { en: 'Restrooms', es: 'Baños' },
    items: [
      { en: 'Toilets and urinals clean inside and out', es: 'Inodoros y urinarios limpios por dentro y por fuera' },
      { en: 'Sinks, counters and mirrors clean', es: 'Lavabos, encimeras y espejos limpios' },
      {
        en: 'Soap, paper towels and toilet tissue stocked',
        es: 'Jabón, toallas de papel y papel higiénico repuestos',
      },
      { en: 'Partitions, walls and fixtures free of marks', es: 'Divisiones, paredes y accesorios sin marcas' },
      { en: 'Floors mopped, no odor', es: 'Pisos trapeados, sin malos olores' },
      { en: 'Trash and sanitary bins emptied', es: 'Basura y contenedores sanitarios vaciados' },
    ],
  },
  {
    area: { en: 'Break room & kitchen', es: 'Comedor y cocina' },
    items: [
      { en: 'Counters, tables and sink clean', es: 'Encimeras, mesas y fregadero limpios' },
      {
        en: 'Appliance exteriors wiped (microwave, fridge, coffee station)',
        es: 'Exterior de electrodomésticos limpio (microondas, refrigerador, cafetera)',
      },
      { en: 'Floors clean', es: 'Pisos limpios' },
      { en: 'Trash and recycling emptied', es: 'Basura y reciclaje vaciados' },
    ],
  },
  {
    area: { en: 'Offices & workspaces', es: 'Oficinas y áreas de trabajo' },
    items: [
      { en: 'Desks and horizontal surfaces dusted', es: 'Escritorios y superficies horizontales sin polvo' },
      { en: 'Carpets vacuumed, edges included', es: 'Alfombras aspiradas, incluidos los bordes' },
      { en: 'Hard floors swept and mopped', es: 'Pisos duros barridos y trapeados' },
      { en: 'Trash emptied', es: 'Basura vaciada' },
    ],
  },
  {
    area: { en: 'Hallways & stairs', es: 'Pasillos y escaleras' },
    items: [
      { en: 'Floors clean, corners and edges included', es: 'Pisos limpios, incluidas esquinas y bordes' },
      { en: 'Handrails, doors and push plates wiped', es: 'Pasamanos, puertas y placas de empuje limpios' },
      { en: 'Vents, ledges and high surfaces dust-free', es: 'Rejillas, repisas y superficies altas sin polvo' },
    ],
  },
  {
    area: { en: 'Throughout', es: 'En todo el edificio' },
    items: [
      { en: 'Light switches and touchpoints disinfected', es: 'Interruptores y puntos de contacto desinfectados' },
      { en: 'Interior glass and partitions free of smudges', es: 'Vidrios interiores y divisiones sin manchas' },
      { en: 'Supplies and janitor closet tidy', es: 'Suministros y cuarto de limpieza ordenados' },
      {
        en: 'Building secured as instructed (lights, doors, alarm)',
        es: 'Edificio asegurado según instrucciones (luces, puertas, alarma)',
      },
    ],
  },
];

/** The standard walkthrough, in one language. */
export function standardInspectionLines(language: SupportedLanguage): InspectionLineSeed[] {
  return STANDARD_INSPECTION_AREAS.flatMap(({ area, items }) =>
    items.map((item) => ({ areaName: area[language], itemName: item[language] })),
  );
}

function clip(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Checklist entries made into lines the inspection will accept.
 *
 * A checklist allows longer names than an inspection line does, and a
 * building whose checklist stacks two templates can list the same task twice.
 * Unclipped, a long name would be refused the first time the walkthrough was
 * saved; doubled, it would be scored twice and count twice. A task with no
 * section is filed under `fallbackArea` rather than dropped.
 */
export function inspectionLinesFrom(
  entries: Iterable<{ areaName: string; itemName: string }>,
  fallbackArea: string,
): InspectionLineSeed[] {
  const seen = new Set<string>();
  const lines: InspectionLineSeed[] = [];
  for (const entry of entries) {
    const itemName = clip(entry.itemName.trim(), INSPECTION_ITEM_NAME_MAX);
    if (!itemName) continue;
    const areaName = clip(entry.areaName.trim() || fallbackArea, INSPECTION_AREA_NAME_MAX);
    const key = `${areaName.toLocaleLowerCase()}\u0000${itemName.toLocaleLowerCase()}`;
    if (seen.has(key)) continue;
    seen.add(key);
    lines.push({ areaName, itemName });
    if (lines.length === INSPECTION_LINES_MAX) break;
  }
  return lines;
}
